import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Grid, Card,
  Skeleton, Chip, IconButton, Tooltip,
} from '@mui/material';
import RedditIcon from '@mui/icons-material/Reddit';
import { getSeasons } from '../services/api';
import BasicVideoModal from '../components/BasicVideoModal';

const SeasonSection = ({ season, onCardClick }) => (
  <Box sx={{ mb: 8 }}>
    {/* Season header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Box sx={{ width: 28, height: 2, bgcolor: 'primary.main', flexShrink: 0 }} />
      <Typography
        variant="overline"
        sx={{ color: 'primary.main', letterSpacing: '0.14em', fontSize: '0.65rem' }}
      >
        Season
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em', lineHeight: 1 }}
      >
        {season.name}
      </Typography>
      <Chip
        label={`${new Date(season.start_date).getFullYear()} – ${new Date(season.end_date).getFullYear()}`}
        size="small"
        sx={{
          bgcolor: 'rgba(201,146,42,0.1)',
          color: 'primary.main',
          fontWeight: 600,
          fontSize: '0.65rem',
          border: '1px solid rgba(201,146,42,0.25)',
        }}
      />
    </Box>

    {season.nominations.length === 0 ? (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        No nominations scraped yet for this season.
      </Typography>
    ) : (
      <Grid container spacing={2}>
        {season.nominations.map((nom) => (
          <Grid item xs={6} sm={4} md={3} lg={2.4} key={nom.id}>
            <Card
              onClick={() => onCardClick(nom)}
              sx={{
                cursor: 'pointer',
                border: 'none',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  aspectRatio: '16/9',
                  bgcolor: '#1A1A1A',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {nom.thumbnail_url ? (
                  <img
                    src={nom.thumbnail_url}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }} />
                )}
                {/* Play icon */}
                <Box
                  sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.3)',
                  }}
                >
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                  </Box>
                </Box>
              </Box>

              {/* Info */}
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      lineHeight: 1.35,
                      letterSpacing: '-0.01em',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {nom.player_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                    {new Date(nom.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </Typography>
                </Box>
                {nom.reddit_url && (
                  <Tooltip title="View on Reddit">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); window.open(nom.reddit_url, '_blank'); }}
                      sx={{ color: 'text.secondary', flexShrink: 0, mt: -0.25, '&:hover': { color: '#FF4500' } }}
                    >
                      <RedditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    )}
  </Box>
);

const Seasons = () => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNom, setModalNom] = useState(null);

  useEffect(() => {
    getSeasons()
      .then(setSeasons)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: { xs: 10, md: 12 }, pb: 10 }}>
      <Container maxWidth="xl">
        {/* Page header */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ width: 28, height: 2, bgcolor: 'primary.main' }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: '0.14em', fontSize: '0.65rem' }}>
              Archive
            </Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em', lineHeight: 1, mb: 1 }}
          >
            Previous Seasons
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 480 }}>
            The most upvoted nominations from past seasons, ordered by community reaction.
          </Typography>
        </Box>

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 10 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} lg={2.4} key={i}>
                <Skeleton variant="rectangular" sx={{ aspectRatio: '16/9', borderRadius: 2, mb: 1 }} />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="50%" />
              </Grid>
            ))}
          </Grid>
        ) : seasons.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              No historical seasons found. Use the admin panel to seed seasons and fetch nominations.
            </Typography>
          </Box>
        ) : (
          seasons.map((season) => (
            <SeasonSection key={season.id} season={season} onCardClick={setModalNom} />
          ))
        )}
      </Container>

      <BasicVideoModal
        open={!!modalNom}
        onClose={() => setModalNom(null)}
        nomination={modalNom}
      />
    </Box>
  );
};

export default Seasons;
